import { useCallback, useEffect, useRef, useState } from 'react'
import { requestRefusalCheck } from '../../../api/refusals'
import { isPromptRequired } from '../utils/generateModelConfig'
import { useTranslation } from 'react-i18next'
import type { Model } from '../../../data/models'
import type { GenerateSettings } from './useGenerateSettings'
import type { UploadedFile, UploadedVideo } from '../utils/uploadHelpers'
import { runGeneration, resolveAudioImageUrls } from '../utils/generationRunners'
import { pillPhraseCaps, stripInactivePillPhrases } from '../../../utils/promptPillPhrases'
import { getAttachedStyleCard } from '../../../api/attachedStyleCard'
import { hasUnresolvedCardTokens } from '../../../utils/variantPrompt'
import { showToast } from '../../../components/Toast'
import { addSticker, claimSticker, listStickers, releaseSticker, updateSticker, useStickers } from '../../../api/generationStickers'
import { captureInputs } from '../logic/inputsSnapshot'
import { saveSourceImage } from '../../../utils/sourceImageDb'
import { getTaskStatus } from '../../../api/images'
import { useGlobalGeneration } from '../../../context/GlobalGenerationContext'
import { confirmMotionRisk } from '../../../components/motionRiskPrompt'
import type { MotionRisk } from './useMotionPrecheck'
import type { GenerationCard } from '../../../types/generationCard'
import {
	buildSnapshot,
	clearPendingSnapshot,
	loadPendingSnapshot,
	persistSourceImage,
	saveToHistory,
	savePendingSnapshot,
	updatePendingTaskId,
	saveGenerateResult,
	loadGenerateResult,
	clearGenerateResult,
} from '../../../context/historyPersistence'

import type { SunoTrack } from '../../../api/music'
import type { AudioOperation } from '../../../context/GenerateContext'
import { translateSunoError } from '../utils/sunoErrors'
import { knownAssetNames } from '../../../api/assets'
import { assetTokensToPlain } from '../../../utils/assetMentions'

/**
 * Запуски, уведённые в фон («Продолжить в фоне»), — по номеру генерации и на всю вкладку, а не на экземпляр виджета:
 * виджет мог перемонтироваться посреди генерации (ушли со страницы и вернулись), и живой запуск старого экземпляра
 * должен узнать, что его отпустили из нового. Иначе он поставил бы свой результат, историю и «готово» поверх
 * следующей генерации.
 */
const detachedGenerations = new Set<string>()

interface UseGenerationFlowParams {
	selectedModel: Model | null
	prompt: string
	settings: GenerateSettings
	sourceTrack?: SunoTrack | null
	audioOperation?: AudioOperation
	user: unknown
	uploadedImage: UploadedFile | null
	swapImage: UploadedFile | null
	uploadedVideo: UploadedVideo | null
	referenceVideo: UploadedVideo | null
	referenceImages: UploadedFile[]
	endImage: UploadedFile | null
	omniReferenceImages: UploadedFile[]
	referenceVideos?: UploadedVideo[]
	referenceAudios?: UploadedFile[]
	/** Именованные фото-слоты карточки (позиция = смысл) — в снимок входов. */
	slotImages?: (UploadedFile | null)[]
	multiShots: { prompt: string; duration: number }[]
	selectedVideoDuration: number
	/** Kling Motion Control: сервер по ролику ждёт отказа Kling — перед запуском спрашиваем «всё равно создать?». */
	motionRisk?: MotionRisk | null
	requiresVideo: boolean
	requiresImage: boolean
	requiresTwoImages: boolean
	supportsMultipleImages: boolean
	isVideo: boolean
	isAudio: boolean
	isPresentation: boolean
	addGeneration: (
		gen: Omit<GenerationCard, 'id' | 'createdAt' | 'type'> & { id?: string },
	) => void
	addNotification: (message: string, type: string) => void
	refreshUser: () => void
	/** Вызывается после успешной audio-генерации чтобы сбросить source-track
	 *  (extend/cover/add_vocals/add_instrumental). Иначе следующая генерация
	 *  молча уйдёт в режим операции над уже использованным треком. */
	onGenerationSuccess?: () => void
	/** Цена на кнопке — уходит в запрос видео (expected_credits): сервер не спишет больше показанного. */
	expectedCredits?: number | null
}

export function useGenerationFlow({
	selectedModel,
	prompt,
	settings,
	user,
	uploadedImage,
	swapImage,
	uploadedVideo,
	referenceVideo,
	referenceImages,
	endImage,
	omniReferenceImages,
	referenceVideos,
	referenceAudios,
	slotImages,
	multiShots,
	selectedVideoDuration,
	motionRisk,
	requiresVideo,
	requiresImage,
	requiresTwoImages,
	supportsMultipleImages,
	isVideo,
	isAudio,
	isPresentation,
	sourceTrack,
	audioOperation,
	addGeneration,
	addNotification,
	refreshUser,
	onGenerationSuccess,
	expectedCredits,
}: UseGenerationFlowParams) {
	const { t } = useTranslation()
	const globalGen = useGlobalGeneration()

	const [isLoading, setIsLoading] = useState(() => {
		if (globalGen.status !== 'running' || !selectedModel) return false
		const snapshot = loadPendingSnapshot()
		if (!snapshot) return false
		return snapshot.modelId === selectedModel.id
	})
	const [error, setError] = useState<string | null>(null)
	const [result, setResult] = useState<string | null>(null)
	const [resultUrls, setResultUrls] = useState<string[] | null>(null)
	const [resultImageUrls, setResultImageUrls] = useState<string[] | null>(null)
	const [lastRequestId, setLastRequestId] = useState<string | null>(null)
	const [userRating, setUserRating] = useState<number | null>(null)
	const [pendingTaskModel, setPendingTaskModel] = useState<string | null>(null)
	// Запуск, который сейчас держит виджет (уведённые в фон — detachedGenerations).
	const currentGenRef = useRef<string | null>(null)
	// Предупреждение Kling Motion Control, на которое клиент уже ответил «всё равно создать»: на тот же ролик, длину и
	// ориентацию второй раз не спрашиваем (повторные запуски, вариации).
	const confirmedRiskRef = useRef<string | null>(null)

	// Текущая категория из модели (image/video/audio/presentation).
	// Используется как ключ для per-category persistence — каждая category
	// хранит свой последний result независимо.
	const currentCategory = selectedModel?.category as
		| 'image'
		| 'video'
		| 'audio'
		| 'presentation'
		| undefined

	// Restore snapshot для текущей категории. При переключении между
	// /bots/audio ↔ /bots/video ↔ /bots/images (через смену модели)
	// state заполняется из сохранённого результата этой категории —
	// юзер возвращается и видит свой прошлый результат на месте.
	// Если для категории нет сохранения — state очищается.
	useEffect(() => {
		if (!currentCategory) return
		const persisted = loadGenerateResult(currentCategory)
		if (persisted) {
			setResult(persisted.result)
			setResultUrls(persisted.resultUrls)
			setResultImageUrls(persisted.resultImageUrls)
			setLastRequestId(persisted.lastRequestId)
			setUserRating(persisted.userRating)
		} else {
			setResult(null)
			setResultUrls(null)
			setResultImageUrls(null)
			setLastRequestId(null)
			setUserRating(null)
		}
	}, [currentCategory])

	// Готовый результат из стикера — в ОТКРЫТЫЙ виджет.
	//
	// Результат ставит тот экземпляр виджета, который запускал генерацию. Если
	// человек ушёл со страницы и вернулся, открытый экземпляр — другой, и о
	// завершении он не узнаёт: зона так и оставалась пустой (владелец, 21.09:
	// «результат в сам виджет не подгрузился»).
	//
	// Стикер переживает и переходы, и пересборку виджета, поэтому берём результат
	// из него. Ставим один раз на стикер (restoredRef) и только в ПУСТУЮ зону:
	// иначе эффект возвращал бы результат, который человек только что убрал.
	const stickers = useStickers()
	const restoredRef = useRef<Set<string>>(new Set())
	useEffect(() => {
		if (result || isLoading || !selectedModel) return
		const mine = stickers.find(
			(s) => s.status === 'done' && !!s.resultUrl && s.modelId === selectedModel.id && !restoredRef.current.has(s.id),
		)
		if (!mine?.resultUrl) return
		restoredRef.current.add(mine.id)
		restoreResult({ result: mine.resultUrl, resultUrls: mine.resultUrls, requestId: mine.requestId })
	// restoreResult пересоздаётся каждый рендер (обычная функция) — в зависимостях
	// он дал бы цикл; остальное перечислено полностью.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stickers, result, isLoading, selectedModel])

	// Lazy-fallback обложек для audio-результата.
	// Срабатывает когда audio-результат загружен (history restore после
	// reload или ранний polling без image_urls), но обложек нет. Дёргаем
	// GET /music/tracks и матчим по audio_url. Safety-check: только для
	// Suno-моделей и только если resultImageUrls явно пустой.
	useEffect(() => {
		const urls = resultUrls || (result ? [result] : [])
		if (!urls.length) return
		const isAudio = !!selectedModel?.id.startsWith('suno-')
		if (!isAudio) return
		const hasCovers = !!resultImageUrls && resultImageUrls.some((u) => !!u)
		if (hasCovers) return
		let cancelled = false
		resolveAudioImageUrls(urls, resultImageUrls || [])
			.then((imgs) => {
				if (cancelled) return
				if (imgs.some((u) => !!u)) {
					setResultImageUrls(imgs)
					// Обновляем snapshot — обложки теперь есть.
					// Безопасно: резолвим только если в map[audio] уже
					// есть наш result (значит категория стабильна).
					if (currentCategory && result && selectedModel) {
						const current = loadGenerateResult(currentCategory)
						if (current && current.result === result) {
							persistResult({
								category: currentCategory,
								result,
								resultUrls,
								resultImageUrls: imgs,
								lastRequestId,
								modelId: selectedModel.id,
								userRating,
							})
						}
					}
				}
			})
			.catch(() => {})
		return () => { cancelled = true }
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resultUrls, result, selectedModel?.id, resultImageUrls])

	function resetResult(): void {
		setResult(null)
		setResultUrls(null)
		setResultImageUrls(null)
		setLastRequestId(null)
		setUserRating(null)
		setError(null)
		// Явная очистка — стираем persisted snapshot только текущей категории,
		// чтобы не терять результаты других категорий.
		if (currentCategory) clearGenerateResult(currentCategory)
	}

	/**
	 * Вернуть в зону результата конкретный результат — возврат по стикеру
	 * незакрытой генерации. Кроме state пишем и persisted snapshot: карточка
	 * применяется при каждом заходе, и без записи следующий же маунт вернул бы
	 * пустую зону.
	 */
	function restoreResult(payload: {
		result: string
		resultUrls?: string[] | null
		requestId?: string | null
	}): void {
		setResult(payload.result)
		setResultUrls(payload.resultUrls ?? null)
		setResultImageUrls(null)
		setLastRequestId(payload.requestId ?? null)
		setUserRating(null)
		setError(null)
		if (currentCategory && selectedModel) {
			persistResult({
				category: currentCategory,
				result: payload.result,
				resultUrls: payload.resultUrls ?? null,
				resultImageUrls: null,
				lastRequestId: payload.requestId ?? null,
				modelId: selectedModel.id,
				userRating: null,
			})
		}
	}

	/**
	 * Сохраняет текущий результат в localStorage для указанной category.
	 * Вызывается ЯВНО из success-path'ов (handleGenerate / resume pending),
	 * а не через useEffect — иначе стейл state со старой category успевает
	 * записаться в новую при навигации между /bots/* страницами.
	 */
	const persistResult = useCallback(
		(args: {
			category: 'image' | 'video' | 'audio' | 'presentation'
			result: string
			resultUrls?: string[] | null
			resultImageUrls?: string[] | null
			lastRequestId?: string | null
			modelId: string
			userRating?: number | null
		}) => {
			saveGenerateResult(args.category, {
				savedAt: Date.now(),
				result: args.result,
				resultUrls: args.resultUrls ?? null,
				resultImageUrls: args.resultImageUrls ?? null,
				lastRequestId: args.lastRequestId ?? null,
				modelId: args.modelId,
				userRating: args.userRating ?? null,
			})
		},
		[],
	)

	// Persist userRating отдельно — он меняется уже после того, как result
	// стабилен, поэтому race'а с category-switch'ем нет.
	useEffect(() => {
		if (!result || !selectedModel || !currentCategory) return
		if (userRating === null) return
		const current = loadGenerateResult(currentCategory)
		if (!current || current.result !== result) return
		persistResult({
			category: currentCategory,
			result,
			resultUrls,
			resultImageUrls,
			lastRequestId,
			modelId: selectedModel.id,
			userRating,
		})
	}, [userRating]) // eslint-disable-line react-hooks/exhaustive-deps

	function getNotificationMessage(model: Model): string {
		const label = model.name || model.id
		return model.category === 'video'
			? t('generate.toast.completedVideo', { label })
			: t('generate.toast.completedImage', { label })
	}

	function savePending(taskId: string, modelId: string): void {
		const modelName = selectedModel?.name || modelId
		updatePendingTaskId(taskId, modelName)
		globalGen.setTask(taskId, modelId, modelName, true)
	}

	/**
	 * «Продолжить в фоне» (план «Ждём ответа нейросети», Ф2): генерация уходит в стикер, виджет свободен для
	 * следующей. Раньше виджет освобождал таймаут опроса; без таймаута долгая генерация держала бы его часами —
	 * решает человек, а не часы. Запуск по-прежнему ждёт ответа сервера и закрывает свой стикер и историю, но в
	 * виджет результат не ставит. Запись для восстановления после перезагрузки снимаем: перезагрузка не должна
	 * снова занять виджет — стикер тогда доведёт стопка (GenerationStickers). После перезагрузки живого запуска
	 * нет (виджет ждёт по записи, GlobalGenerationContext) — отпускаем так же, стикер ведёт стопка.
	 */
	const detachGeneration = useCallback(() => {
		const snapshot = loadPendingSnapshot()
		// Живой запуск другого экземпляра виджета (перемонтировали) узнаём по записи о генерации.
		const id = currentGenRef.current ?? snapshot?.generationId ?? null
		if (id) detachedGenerations.add(id)
		// Живого запуска во вкладке нет (новая вкладка, виджет ждал по записи) и стопка о генерации не знает — ставим
		// стикер из записи: генерация уходит в стикер, и его доведёт опрос стопки (GenerationStickers).
		if (!currentGenRef.current && snapshot?.taskId && !listStickers().some((st) => st.id === snapshot.generationId)) {
			addSticker({
				id: snapshot.generationId,
				status: 'running',
				origin: snapshot.origin ?? { path: window.location.pathname },
				modelName: snapshot.modelName,
				modelId: snapshot.modelId,
				sourceImageDbKey: snapshot.sourceImageDbKey,
				requestId: snapshot.taskId,
				startedAt: snapshot.startedAt,
			})
		}
		currentGenRef.current = null
		clearPendingSnapshot()
		globalGen.clearDone()
		setIsLoading(false)
		setPendingTaskModel(null)
	}, [globalGen])

	function validate(): string | null {
		if (!selectedModel) return t('generate.validation.selectModel')
		if (!user) return t('generate.validation.loginRequired')
		if (selectedModel.category === 'audio') {
			// Suno music: 200 char limit без custom_mode (используется как «описание»),
			// 5000 — с custom_mode (полная лирика). Submode 'sounds' идёт через
			// отдельный endpoint и под этот лимит не попадает.
			if (settings.audioSubmode === 'tts') {
				if (prompt.length > 500) return 'Текст озвучки не может превышать 500 символов'
				return null
			}
			if (settings.audioSubmode !== 'sounds') {
				const limit = settings.audioCustomMode ? 5000 : 200
				if (prompt.length > limit) {
					return settings.audioCustomMode
						? 'Текст песни не может превышать 5000 символов'
						: 'Промпт превышает 200 символов. Включите режим «Кастомный» для ввода полного текста песни.'
				}
			}
			return null
		}
		if (requiresImage && !uploadedImage?.url) return t('generate.validation.uploadImage')
		if (requiresTwoImages && (!uploadedImage?.url || !swapImage?.url))
			return t('generate.validation.uploadBothImages')
		if (requiresVideo && !uploadedVideo?.url) return t('generate.validation.uploadVideo')
		return null
	}

	const prevGlobalStatus = useRef(globalGen.status)
	useEffect(() => {
		const wasRunning = prevGlobalStatus.current === 'running'
		prevGlobalStatus.current = globalGen.status

		if (!wasRunning) return

		if (globalGen.status === 'done') {
			setIsLoading(false)
			setPendingTaskModel(null)
			const snapshot = loadPendingSnapshot()
			if (!snapshot || !snapshot.taskId) return
			getTaskStatus(snapshot.taskId)
				.then((s) => {
					if (s.status === 'completed' && s.result_url) {
						// Для audio — сохраняем ОБА трека (result_urls) + обложки.
						// Для не-audio — снапшот как раньше.
						saveToHistory({
							resultUrl: s.result_url,
							resultUrls: s.result_urls ?? null,
							imageUrls: s.image_urls ?? null,
							snapshot,
							addGeneration,
						})
						clearPendingSnapshot()
						if (selectedModel && snapshot.modelId === selectedModel.id) {
							const newResultUrls = s.result_urls && s.result_urls.length > 0
								? s.result_urls
								: null
							const newImageUrls = s.image_urls && s.image_urls.length > 0
								? s.image_urls
								: null
							setResult(s.result_url)
							if (newResultUrls) setResultUrls(newResultUrls)
							if (newImageUrls) setResultImageUrls(newImageUrls)
							setLastRequestId(snapshot.taskId!)
							addNotification(getNotificationMessage(selectedModel), 'info')
							// Явный persist в map[category]
							const cat = selectedModel.category as 'image' | 'video' | 'audio' | 'presentation'
							persistResult({
								category: cat,
								result: s.result_url,
								resultUrls: newResultUrls,
								resultImageUrls: newImageUrls,
								lastRequestId: snapshot.taskId!,
								modelId: selectedModel.id,
							})
						}
						refreshUser()
					}
				})
				.catch(() => {})
		} else if (globalGen.status === 'error') {
			setIsLoading(false)
			setPendingTaskModel(null)
			setError(prev => prev ?? globalGen.errorMessage ?? t('generate.error.generationFailed'))
			clearPendingSnapshot()
		}
	}, [globalGen.status]) // eslint-disable-line react-hooks/exhaustive-deps

	async function handleGenerate(): Promise<void> {
		// Для параметрических audio-операций (extend / cover / add_vocals /
		// add_instrumental) prompt опциональный — Suno сам напишет текст
		// под стиль. Юзер может хотеть поменять только жанр или вокал.
		const isParametricAudio = !!(
			sourceTrack && audioOperation && audioOperation !== 'generate'
		)
		const needsPrompt = isPromptRequired(selectedModel ?? null, { requiresVideo, isParametricAudio })
		if ((!prompt.trim() && needsPrompt) || isLoading) return
		// Токены карточки в промпте ({variant}, {value}, {photoN}) — стиль не выбран или поле пусто: не отправляем,
		// иначе нейросеть получит их текстом, а человек заплатит (аудит мультистилей 22.10).
		if (hasUnresolvedCardTokens(prompt)) {
			showToast('Выберите стиль и заполните поле карточки', 'error')
			return
		}

		const validationError = validate()
		if (validationError) {
			setError(validationError)
			return
		}

		// Kling Motion Control: замер позы на сервере (калибровка 12.10) ждёт отказа Kling — решает клиент.
		if (motionRisk && confirmedRiskRef.current !== motionRisk.key) {
			if (!(await confirmMotionRisk(motionRisk.text))) return
			confirmedRiskRef.current = motionRisk.key
		}

		setError(null)
		setIsLoading(true)
		setResult(null)
		setResultUrls(null)

		// Genie-анимация «pin улетает в result-slot» при старте операции
		// над треком (extend / cover / add_vocals / add_instrumental).
		// SunoSourceTrackPin слушает это событие и анимируется. Делаем
		// ДО runGeneration чтобы визуально среагировать мгновенно.
		if (isAudio && sourceTrack && audioOperation && audioOperation !== 'generate') {
			window.dispatchEvent(new CustomEvent('suno-extend-fly'))
		}

		// Скелет-карточка трека на #1 ленты для ОБЫЧНОЙ генерации музыки (не
		// операция над треком, не tts/sounds). Сигналим старт → BotsAudioLibrary
		// рисует pending-строку (название + спиннер на месте обложки). Снимается
		// по 'suno-track-generated' (успех) или 'music-generation-failed' (ошибка).
		const isMusicTrackGen =
			isAudio &&
			audioOperation === 'generate' &&
			settings.audioSubmode !== 'tts' &&
			settings.audioSubmode !== 'sounds'
		if (isMusicTrackGen) {
			window.dispatchEvent(
				new CustomEvent('music-generating', {
					detail: {
						title: settings.audioTitle?.trim() || 'Новый трек',
						modelId: selectedModel!.id,
					},
				}),
			)
		}

		const sourceFile: File | null = isAudio
			? null
			: (uploadedImage?.file ?? referenceImages[0]?.file ?? swapImage?.file ?? null)

		const snapshotDraft = buildSnapshot({
			prompt,
			isVideo,
			isAudio,
			isPresentation,
			modelId: selectedModel!.id,
			modelName: selectedModel!.name,
			settings: {
				aspectRatio: settings.aspectRatio,
				resolution: settings.resolution,
				duration: settings.duration,
				outputFormat: settings.outputFormat,
			},
			audioTitle: settings.audioTitle,
			audioStyle: settings.audioStyle,
			audioInstrumental: settings.audioInstrumental,
			audioVocalGender: settings.audioVocalGender,
			audioCustomMode: settings.audioCustomMode,
			presentationMode: settings.presentation?.mode,
			// Откуда запустили: путь + карточка. Читаем ЗДЕСЬ, в момент старта —
			// позже пин уже может быть снят (виджет отпускает его на уходе со
			// страницы), а стикеру нужно вернуть человека именно на это место.
			origin: (() => {
				const card = getAttachedStyleCard()
				return {
					path: window.location.pathname + window.location.search,
					cardId: card?.id ?? undefined,
					cardTitle: card?.title,
					cardPreviewUrl: card?.previewUrl,
				}
			})(),
		})

		// Запуск держит виджет с этой секунды: «Продолжить в фоне» доступна уже сейчас, до сохранения входов.
		const genId = snapshotDraft.generationId
		const detached = () => detachedGenerations.has(genId)
		currentGenRef.current = genId
		// Запись о генерации — своя: на её месте после «Продолжить в фоне» может лежать следующая.
		const ownSnapshot = () => {
			const saved = loadPendingSnapshot()
			return saved?.generationId === genId ? saved : null
		}
		const clearOwnSnapshot = () => {
			if (ownSnapshot()) clearPendingSnapshot()
		}

		const sourceImageDbKey = await persistSourceImage(
			snapshotDraft.generationId,
			sourceFile,
		)
		const snapshot = { ...snapshotDraft, sourceImageDbKey }
		// Снимок ВСЕХ входов — для возврата по стикеру. Одного sourceImageDbKey
		// мало: у видео-трендов фото лежат в именованных слотах, а второе фото,
		// видео и аудио одиночный ключ в свои каналы не вернёт (владелец 22.09:
		// «у видео теряются фото»). Первичный файл второй раз не пишем.
		const inputs = await captureInputs(
			snapshot.generationId,
			{
				uploadedImage,
				swapImage,
				endImage,
				uploadedVideo,
				referenceVideo,
				slotImages: slotImages ?? [],
				referenceImages,
				omniReferenceImages,
				referenceVideos: referenceVideos ?? [],
				referenceAudios: referenceAudios ?? [],
				selectedVideoDuration,
			},
			{
				save: saveSourceImage,
				primary: sourceFile && sourceImageDbKey ? { file: sourceFile, key: sourceImageDbKey } : undefined,
			},
		)
		// Отпустили, пока сохранялись входы, — виджет не занимаем снова: генерацию ведёт стикер.
		if (!detached()) {
			savePendingSnapshot(snapshot)
			// Отмечаем «идёт» СРАЗУ, не дожидаясь taskId: у картиночных моделей его
			// не бывает вовсе (ответ одним запросом), и без этого глобальный статус
			// всю генерацию оставался idle — вернувшийся на страницу человек видел
			// пустую зону вместо прелоадера.
			globalGen.setRunning(snapshot.modelName)
		}
		// Стикер ставим здесь же: снимок — единственное место, где разом есть и
		// generationId, и «откуда запустили». Дальше стикер живёт сам по себе,
		// переживая уход со страницы и снятие пина.
		addSticker({
			id: snapshot.generationId,
			status: 'running',
			origin: snapshot.origin ?? { path: window.location.pathname },
			modelName: snapshot.modelName,
			modelId: snapshot.modelId,
			sourceImageDbKey: snapshot.sourceImageDbKey,
			inputs,
			startedAt: snapshot.startedAt,
		})
		claimSticker(genId)

		try {
			// Фразы-пилюли входов, которые ЭТА модель не потребляет, в запрос не идут
			// (в состоянии текст остаётся — пилюли вернутся при возврате к модели).
			// Упоминания ассетов (@{uuid}@) понимают только ручки картинок и видео — сервер превращает их в имя и номера
			// фото. Музыка, озвучка и презентации получили бы uuid как текст (промпт у виджетов общий, и чип мог
			// приехать из соседнего раздела) — им отдаём имена.
			const promptText = isAudio || isPresentation ? assetTokensToPlain(prompt, knownAssetNames()) : prompt
			const promptForModel = stripInactivePillPhrases(
				promptText,
				pillPhraseCaps(selectedModel, settings?.seedanceMode, undefined, supportsMultipleImages),
			)
			const genResult = await runGeneration({
				model: selectedModel!,
				prompt: promptForModel,
				settings,
				uploadedImage,
				swapImage,
				uploadedVideo,
				referenceVideo,
				referenceImages,
				endImage,
				omniReferenceImages,
				referenceVideos,
				referenceAudios,
				multiShots,
				selectedVideoDuration,
				requiresVideo,
				requiresTwoImages,
				supportsMultipleImages,
				// Номер генерации — и стикеру: по нему стопка доведёт генерацию, если вкладку перезагрузят.
				onPendingSave: (taskId: string, modelId: string) => {
					updateSticker(genId, { requestId: taskId })
					if (!detached()) savePending(taskId, modelId)
				},
				sourceTrack,
				audioOperation,
				// Демо-трек прикреплённой карточки → стилевой референс генерации.
				// Снапшот НА МОМЕНТ клика «Создать»; гейт Mureka — в runner'е.
				audioReferenceUrl: isAudio ? getAttachedStyleCard()?.audioUrl : undefined,
				expectedCredits,
			})

			// Уведённый в фон запуск виджет не трогает: там уже может идти следующая генерация.
			const inBackground = detached()
			if (genResult.ok && genResult.resultUrl) {
				const newResultUrls = genResult.resultUrls && genResult.resultUrls.length > 0
					? genResult.resultUrls
					: null
				const newImageUrls = genResult.imageUrls && genResult.imageUrls.length > 0
					? genResult.imageUrls
					: null
				if (!inBackground) {
					setResult(genResult.resultUrl)
					if (newResultUrls) setResultUrls(newResultUrls)
					if (newImageUrls) setResultImageUrls(newImageUrls)
					if (genResult.requestId) {
						setLastRequestId(genResult.requestId)
						setUserRating(null)
					}
				}

				// Явный persist в map[category] — никаких race с эффектами.
				if (selectedModel && !inBackground) {
					const cat = selectedModel.category as 'image' | 'video' | 'audio' | 'presentation'
					persistResult({
						category: cat,
						result: genResult.resultUrl,
						resultUrls: newResultUrls,
						resultImageUrls: newImageUrls,
						lastRequestId: genResult.requestId ?? null,
						modelId: selectedModel.id,
					})
				}

				// Запись о генерации в фоне уже снята (а на её месте может лежать следующая) — берём свой снимок.
				const finalSnapshot = (inBackground ? null : ownSnapshot()) ?? snapshot
				saveToHistory({
					resultUrl: genResult.resultUrl,
					resultUrls: genResult.resultUrls ?? null,
					imageUrls: genResult.imageUrls ?? null,
					snapshot: finalSnapshot,
					addGeneration,
					// Синхронный путь: request id приходит прямо в ответе (у snapshot
					// taskId может не быть). Polling-путь берёт snapshot.taskId сам.
					requestId: genResult.requestId ?? null,
				})
				if (!inBackground) clearOwnSnapshot()

				// Картинку результата кладём в стикер: по стопке сразу видно, ЧТО
				// получилось, ещё до клика. У видео/аудио превью нет — остаётся
				// обложка карточки.
				updateSticker(snapshot.generationId, {
					status: 'done',
					resultUrl: genResult.imageUrls?.[0] ?? genResult.resultUrl ?? undefined,
					resultUrls: genResult.resultUrls ?? null,
					requestId: genResult.requestId ?? null,
				})
				if (!inBackground) globalGen.setDone()
				refreshUser()
				addNotification(getNotificationMessage(selectedModel!), 'info')
				if (!inBackground) onGenerationSuccess?.()

				// Уведомляем Library-страницу что появился новый трек — она сама
				// перезагрузит ленту без F5. Для МУЗЫКИ шлём и для Suno, И для
				// Mureka (раньше гейт был только на 'suno-' → Mureka-трек не
				// подтягивался без перезагрузки). TTS уходит на свою вкладку.
				if (settings.audioSubmode === 'tts') {
					window.dispatchEvent(new CustomEvent('tts-generated'))
				} else if (selectedModel?.category === 'audio' && settings.audioSubmode !== 'sounds') {
					window.dispatchEvent(new CustomEvent('suno-track-generated'))
				} else if (selectedModel?.category === 'presentation') {
					// Студия презентаций (/bots/tools/slides) подтягивает новую деку
					// в открытую папку и открывает её на столе — раньше событие слал
					// только GammaEditSheet, и после генерации из виджета папка
					// обновлялась лишь по F5.
					window.dispatchEvent(new CustomEvent('presentation-generated'))
				}
			} else if (!genResult.ok) {
				requestRefusalCheck()
				// Текст ошибки — и в setFailed: юзер мог уехать со страницы старта
				// (хэндофф витрин в студию), его новый инстанс покажет ошибку из
				// globalGen.errorMessage — без текста там оставалась безликая
				// «Ошибка генерации» даже у «не хватило токенов».
				if (inBackground) {
					// Ошибку покажет стикер ниже; виджет занят другим.
				} else if (genResult.isNsfwWarning) {
					// Окно предупреждения — общее для всех разделов (RefusalNoticeHost): сервер уже записал его.
					globalGen.setFailed('Запрос не прошёл модерацию. Попробуйте переформулировать.')
				} else {
					const isSuno = !!selectedModel?.id.startsWith('suno-')
					const rawErr = genResult.error || t('generate.error.generationError')
					const shownErr = isSuno ? translateSunoError(rawErr) : rawErr
					setError(shownErr)
					globalGen.setFailed(shownErr)
				}
				if (!inBackground) clearOwnSnapshot()
				updateSticker(snapshot.generationId, {
					status: 'error',
					error: genResult.error || undefined,
				})
				if (isMusicTrackGen) window.dispatchEvent(new CustomEvent('music-generation-failed'))
			}
		} catch (err) {
			requestRefusalCheck()
			const rawErr = err instanceof Error ? err.message : t('generate.error.generationError')
			const isSuno = !!selectedModel?.id.startsWith('suno-')
			const shownErr = isSuno ? translateSunoError(rawErr) : rawErr
			updateSticker(snapshot.generationId, { status: 'error', error: shownErr })
			if (!detached()) {
				setError(shownErr)
				clearOwnSnapshot()
				globalGen.setFailed(shownErr)
			}
			if (isMusicTrackGen) window.dispatchEvent(new CustomEvent('music-generation-failed'))
		} finally {
			releaseSticker(genId)
			// Запуск в фоне виджет уже отпустил — и мог занять снова следующей генерацией.
			if (detached()) {
				detachedGenerations.delete(genId)
			} else {
				if (currentGenRef.current === genId) currentGenRef.current = null
				setIsLoading(false)
			}
		}
	}

	return {
		isLoading,
		error,
		setError,
		clearError: useCallback(() => setError(null), []),
		result,
		setResult,
		resultUrls,
		setResultUrls,
		resultImageUrls,
		setResultImageUrls,
		lastRequestId,
		setLastRequestId,
		userRating,
		setUserRating,
		pendingTaskModel,
		handleGenerate,
		detachGeneration,
		resetResult,
		restoreResult,
	}
}
